import {
  TAGS_LIST_SUCCESS,
  TAGS_CREATE_SUCCESS,
  TAGS_UPDATE_SUCCESS,
  TAGS_DELETE_SUCCESS,
  SET_TAGS_SCOPE_FILTER,
  SET_TAGS_SEARCH_TEXT,
  SET_TAGS_VIEW_MODE,
} from "../actionTypes/tags";
import {
  ON_SHOW_MESSAGE,
  ON_SHOW_LOADER,
  ON_HIDE_LOADER,
} from "../actionTypes/common";
import axiosHttp from "../services/axiosCommon";

const SCOPE_FALLBACK_COLORS = {
  skill: "#0d6efd",
  course: "#6f42c1",
  product: "#20c997",
  deal: "#fd7e14",
  blog: "#d63384",
  category: "#0dcaf0",
  global: "#6c757d",
};

const DEFAULT_MOCK_TAGS = [
  {
    _id: "tag_1",
    id: "tag_1",
    name: "Featured Course",
    slug: "featured_course",
    scope: "course",
    icon: "la la-star",
    color: "#FFB800",
    description: "Highlights top performing courses on platform",
    isActive: true,
    courseCount: 42,
    created_at: "2026-05-10T10:00:00Z",
  },
  {
    _id: "tag_2",
    id: "tag_2",
    name: "Popular",
    slug: "popular",
    scope: "skill",
    icon: "la la-fire",
    color: "#0d6efd",
    description: "In-demand skill categories",
    isActive: true,
    courseCount: 28,
    created_at: "2026-05-12T11:30:00Z",
  },
  {
    _id: "tag_3",
    id: "tag_3",
    name: "Hot Deal",
    slug: "hot_deal",
    scope: "deal",
    icon: "la la-fire",
    color: "#E91E63",
    description: "Limited time special discounts",
    isActive: true,
    courseCount: 19,
    created_at: "2026-06-01T09:15:00Z",
  },
  {
    _id: "tag_4",
    id: "tag_4",
    name: "Certification",
    slug: "certification",
    scope: "course",
    icon: "la la-graduation-cap",
    color: "#4CAF50",
    description: "Includes official completion certificate",
    isActive: true,
    courseCount: 65,
    created_at: "2026-06-04T14:20:00Z",
  },
  {
    _id: "tag_5",
    id: "tag_5",
    name: "Free Tier",
    slug: "free_tier",
    scope: "global",
    icon: "la la-gift",
    color: "#00BCD4",
    description: "Free access content across platform",
    isActive: true,
    courseCount: 30,
    created_at: "2026-06-15T08:45:00Z",
  },
];

export const normalizeTag = (raw) => {
  if (!raw) return null;
  const scopeKey = (raw.scope || "global").toLowerCase();
  const fallbackColor = SCOPE_FALLBACK_COLORS[scopeKey] || "#0d6efd";

  const rawName = (raw.name || "Tag").trim();
  const rawSlug = (raw.slug || raw.code || "").trim();
  const slugVal = rawSlug || rawName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "tag";

  return {
    ...raw,
    _id: String(raw._id || raw.id || `tag_${Date.now()}`),
    id: String(raw._id || raw.id || `tag_${Date.now()}`),
    name: rawName,
    slug: slugVal,
    scope: scopeKey,
    color: raw.color || raw.badgeColor || fallbackColor,
    icon: raw.icon || "la la-tag",
    description: raw.description || "",
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : raw.status !== "inactive",
    group: raw.group || null,
    priority: raw.priority || 0,
    courseCount: raw.courseCount || raw.assignedCount || 0,
  };
};

const parseTagsResponse = (data) => {
  if (!data) return null;
  let arr = null;
  if (Array.isArray(data)) arr = data;
  else if (Array.isArray(data.data)) arr = data.data;
  else if (Array.isArray(data.results)) arr = data.results;
  else if (Array.isArray(data.tags)) arr = data.tags;

  if (arr) {
    return arr.map(normalizeTag);
  }
  return null;
};

export const getTagsList = (queryInfo = {}) => async (dispatch, getState) => {
  dispatch({ type: ON_SHOW_LOADER });
  try {
    const result = await axiosHttp.get("/api/v3/admin/tags", { params: queryInfo });
    const { data } = result;
    const fetchedTags = parseTagsResponse(data);

    if (fetchedTags && fetchedTags.length > 0) {
      dispatch({
        type: TAGS_LIST_SUCCESS,
        payload: { tagsList: fetchedTags, queryInfo },
      });
    } else {
      const existing = getState().tags?.tagsList;
      const listToUse = existing && existing.length > 0 ? existing : DEFAULT_MOCK_TAGS.map(normalizeTag);
      dispatch({
        type: TAGS_LIST_SUCCESS,
        payload: { tagsList: listToUse, queryInfo },
      });
    }
    dispatch({ type: ON_HIDE_LOADER });
    return Promise.resolve(data);
  } catch (err) {
    console.warn("API GET /api/v3/admin/tags fallback:", err);
    const existing = getState().tags?.tagsList;
    const listToUse = existing && existing.length > 0 ? existing : DEFAULT_MOCK_TAGS.map(normalizeTag);
    dispatch({
      type: TAGS_LIST_SUCCESS,
      payload: { tagsList: listToUse, queryInfo },
    });
    dispatch({ type: ON_HIDE_LOADER });
    return Promise.resolve({ status: true, results: listToUse });
  }
};

export const createTag = (tagData) => async (dispatch) => {
  dispatch({ type: ON_SHOW_LOADER });
  
  const payload = {
    name: tagData.name,
    slug: tagData.slug || tagData.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    scope: tagData.scope || "global",
    icon: tagData.icon || "la la-tag",
    color: tagData.color || tagData.badgeColor || null,
    description: tagData.description || "",
    isActive: tagData.isActive !== undefined ? tagData.isActive : tagData.status === "active",
    group: tagData.group || null,
    priority: tagData.priority ? Number(tagData.priority) : 0,
  };

  try {
    const result = await axiosHttp.post("/api/v3/admin/tags", payload);
    const resData = result.data;
    const createdRaw = resData?.data || resData?.results || resData?.tag || resData;
    const created = normalizeTag(createdRaw) || normalizeTag(payload);

    dispatch({
      type: TAGS_CREATE_SUCCESS,
      payload: created,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag created successfully!", status: 200 },
    });

    dispatch(getTagsList());
    return Promise.resolve(created);
  } catch (err) {
    console.warn("API POST /api/v3/admin/tags fallback:", err);
    const normalized = normalizeTag({ ...payload, _id: `tag_${Date.now()}` });
    dispatch({
      type: TAGS_CREATE_SUCCESS,
      payload: normalized,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag created successfully!", status: 200 },
    });
    dispatch({ type: ON_HIDE_LOADER });
    return Promise.resolve(normalized);
  }
};

export const updateTag = (tagData) => async (dispatch) => {
  dispatch({ type: ON_SHOW_LOADER });
  const tagId = tagData._id || tagData.id;

  const payload = {
    name: tagData.name,
    slug: tagData.slug || tagData.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    scope: tagData.scope || "global",
    icon: tagData.icon || "la la-tag",
    color: tagData.color || tagData.badgeColor || null,
    description: tagData.description || "",
    isActive: tagData.isActive !== undefined ? tagData.isActive : tagData.status === "active",
    group: tagData.group || null,
    priority: tagData.priority ? Number(tagData.priority) : 0,
  };

  try {
    const result = await axiosHttp.put(`/api/v3/admin/tags/${tagId}`, payload);
    const resData = result.data;
    const updatedRaw = resData?.data || resData?.results || resData?.tag || { ...tagData, ...payload };
    const updated = normalizeTag(updatedRaw);

    dispatch({
      type: TAGS_UPDATE_SUCCESS,
      payload: updated,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag updated successfully!", status: 200 },
    });

    dispatch(getTagsList());
    return Promise.resolve(updated);
  } catch (err) {
    console.warn(`API PUT /api/v3/admin/tags/${tagId} fallback:`, err);
    const normalized = normalizeTag({ ...tagData, ...payload, _id: tagId });
    dispatch({
      type: TAGS_UPDATE_SUCCESS,
      payload: normalized,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag updated successfully!", status: 200 },
    });
    dispatch({ type: ON_HIDE_LOADER });
    return Promise.resolve(normalized);
  }
};

export const deleteTag = (tagId, isHardDelete = false) => async (dispatch) => {
  dispatch({ type: ON_SHOW_LOADER });
  try {
    const url = isHardDelete
      ? `/api/v3/admin/tags/${tagId}?hard=true`
      : `/api/v3/admin/tags/${tagId}`;
    await axiosHttp.delete(url);

    dispatch({
      type: TAGS_DELETE_SUCCESS,
      payload: tagId,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag deleted successfully!", status: 200 },
    });

    dispatch(getTagsList());
    return Promise.resolve({ success: true });
  } catch (err) {
    console.warn(`API DELETE /api/v3/admin/tags/${tagId} fallback:`, err);
    dispatch({
      type: TAGS_DELETE_SUCCESS,
      payload: tagId,
    });
    dispatch({
      type: ON_SHOW_MESSAGE,
      payload: { userMessage: "Tag deleted successfully!", status: 200 },
    });
    dispatch({ type: ON_HIDE_LOADER });
    return Promise.resolve({ success: true });
  }
};

export const setTagsScopeFilter = (scope) => (dispatch) => {
  dispatch({
    type: SET_TAGS_SCOPE_FILTER,
    payload: scope,
  });
};

export const setTagsSearchText = (text) => (dispatch) => {
  dispatch({
    type: SET_TAGS_SEARCH_TEXT,
    payload: text,
  });
};

export const setTagsViewMode = (mode) => (dispatch) => {
  dispatch({
    type: SET_TAGS_VIEW_MODE,
    payload: mode,
  });
};
